import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";

export default function NotificationBell({ iconOnly = false }) {
  const { user } = useAuth();
  const notifCtx = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/notifications").then(r => setItems(r.data.slice(0, 6))).finally(() => setLoading(false));
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!user) return null;
  const unread = notifCtx?.unreadCount || 0;

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      notifCtx?.refreshUnread();
    }
    navigate(n.url || "/notifications");
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="notification-bell"
        aria-label="Notifications"
        className={`relative flex items-center justify-center rounded-full text-black hover:bg-slate-100 transition-colors ${iconOnly ? "w-8 h-8" : "w-9 h-9"}`}
      >
        <Bell className="w-[18px] h-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">Notifications</span>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="text-xs text-slate-500 hover:text-black"
            >
              View all
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-center text-xs text-slate-400">Loading...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-slate-400">No notifications yet.</p>
            ) : items.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors ${!n.read ? "bg-[#E8F0FE]/50" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#1D6FF2] mt-1.5 shrink-0" />}
                  <div className={n.read ? "pl-3.5" : ""}>
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
